import { fabric } from "fabric";
import { useState, useEffect, useCallback } from "react";

import { TaskMapPin } from "../TaskMapPin";
import useDeleteKeyHandler from "./useDeleteKeyHandler";
import useCanvasState from "./useCanvasState";
import useWorksiteTask from "../../hooks/useWorksiteTask";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";

import { COLORS } from "../constants";

function addPin(event, canvas, worksiteTask) {
  const pointer = canvas.getPointer(event.e);
  // Remove the existing pin, if any
  canvas.getObjects().forEach((obj) => {
    if (obj.name === "pin") {
      canvas.remove(obj);
    }
  });

  TaskMapPin(canvas, pointer.x, pointer.y, worksiteTask);
}

function setupDrawingMode(canvas, color) {
  canvas.isDrawingMode = true;
  canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
  canvas.freeDrawingBrush.color = `rgba(${color}, 0.5)`;
  canvas.freeDrawingBrush.width = 30;
}

function deleteSelectedObject(canvas) {
  const activeObject = canvas.getActiveObject();
  if (activeObject) {
    canvas.remove(activeObject);
    canvas.requestRenderAll();
  }
}

function showBalloon(canvas, object, message, max_length=undefined) {
  // set font size based on canvas zoom and width
  // const pointer = canvas.getPointer(evt);
  // position based on mouse position
  // const title =
  //   message.slice(0, max_length) +
  //   (max_length && message.length > max_length ? "..." : "");
  // insert new line after max_length characters
  const regex = new RegExp(`(.{${max_length}})`, "g");
  const title = message.replace(regex, "$1\n");
  
  const left = object.left + object.width / 2;
  const top = object.top + object.height / 2;
  const balloon = new fabric.Text(title, {
    left,
    top,
    fontSize: 22,
    width: 50,
    textAlign: "left",
    fontFamily: 'sans-serif',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    fill: 'white',
    name: 'balloon',
    padding: 10,
    // disable mouse events
    selectable: false,
    evented: false,
    scaleX: 1 / canvas.getZoom(),
    scaleY: 1 / canvas.getZoom(),
  });

  canvas.add(balloon);
  canvas.renderAll();
}

const useCanvasMode = (
  canvas,
  mode,
  preSignedUrl,
  color,
  setMode,
  taskId = null,
  taskMapId,
  onSave,
  onClickElement = () => {},
  checkRequirements = () => true,
) => {
  const [hasSelectedObject, setHasSelectedObject] = useState(false);
  const { worksiteTask } = useWorksiteTask(taskId);
  const countryApiRequest = useCountryAxiosInstance();

  const [cacheBaloonResponses, setCacheBaloonResponses] = useState({});

  useDeleteKeyHandler(canvas, deleteSelectedObject);
  const { serializeCanvasState, saveCanvasState } = useCanvasState(
    canvas,
    taskId,
    taskMapId
  );

  const handleSaveButtonClick = useCallback(async () => {
    const canvasState = serializeCanvasState();
    if (!checkRequirements(canvasState)) {
      return;
    }
    await saveCanvasState(canvasState);
    onSave(canvasState);
  }, [saveCanvasState, serializeCanvasState, onSave, checkRequirements]);

  useEffect(() => {
    if (!preSignedUrl || !canvas) {
      return;
    }

    // background image
    canvas.__eventListeners = {};

    canvas.on("mouse:over", async function (opt) {
      // clone opt.e
      const event = Object.assign({}, opt.e);
      if (!event) {
        return;
      }
      if (opt.target?.tmd) {
        const task_id = opt.target.tmd.id_task;
        if (cacheBaloonResponses[task_id]) {
          showBalloon(canvas, opt.target, cacheBaloonResponses[task_id].des_name, 30);
          return;
        }
        const response = await countryApiRequest.get(
          `/worksites_tasks/${task_id}/extended`
        )
        showBalloon(canvas, opt.target, response.des_name, 30);
        // create baloon displaying the task name
        setCacheBaloonResponses({
          ...cacheBaloonResponses,
          [task_id]: response,
        });
      }
    })
    
    canvas.on('mouse:out', (options) => {
      if (options.target && options.target?.tmd) {
        canvas.remove(canvas.getObjects().find(obj => obj.name === 'balloon'));
        canvas.renderAll();
      }
    });

    canvas.on("mouse:down", function (opt) {
      if (opt.target?.tmd) {
        onClickElement(opt.target.name, opt.target.tmd);
      }
    });

    canvas.on({
      "selection:updated": () => setHasSelectedObject(true),
      "selection:created": () => setHasSelectedObject(true),
      "selection:cleared": () => setHasSelectedObject(false),
    });

    if (mode === "delete") {
      deleteSelectedObject(canvas);
      canvas.renderAll();
      setMode("select");
    }

    if (mode === "save") {
      handleSaveButtonClick();
      setMode("select");
    }

    if (mode === "move") {
      // zooming
      canvas.on("touch:gesture", function (e) {
        if (e.e.touches && e.e.touches.length === 2) {

          const touch1 = e.e.touches[0];
          const touch2 = e.e.touches[1];
          const centerX = (touch1.clientX + touch2.clientX) / 2;
          const centerY = (touch1.clientY + touch2.clientY) / 2;
          const dist = Math.sqrt(
            Math.pow(touch1.clientX - touch2.clientX, 2) +
            Math.pow(touch1.clientY - touch2.clientY, 2)
          );
          if (e.e.type === "touchstart" || e.e.type === "gesturestart") {
            canvas.zoomToPoint(
              new fabric.Point(centerX, centerY),
              canvas.getZoom()
            );
            canvas.lastDist = dist;
          } else {
            canvas.zoomToPoint(
              new fabric.Point(centerX, centerY),
              canvas.getZoom() * (dist / canvas.lastDist)
            );
            canvas.lastDist = dist;
          }
          canvas.requestRenderAll();
        }
      });

      canvas.on("mouse:wheel", function (opt) {
        const delta = opt.e.deltaY;
        let zoom = canvas.getZoom();
        zoom *= 0.999 ** delta;
        if (zoom > 20) zoom = 20;
        if (zoom < 0.1) zoom = 0.1;
        canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
        opt.e.preventDefault();
        opt.e.stopPropagation();
      });
      // panning
      canvas.on("mouse:down", function (opt) {
        const evt = opt.e;

        this.isDragging = true;
        this.selection = false;
        if (evt.type === "mousedown") {
          // avoid panning background image outside canvas bounds
          // cosidering the zoom level
          this.lastPosX = evt.clientX;
          this.lastPosY = evt.clientY;
        }
        if (
          evt.type === "touchstart" &&
          evt.touches &&
          evt.touches.length === 1
        ) {
          this.lastPosX = evt.touches[0].clientX;
          this.lastPosY = evt.touches[0].clientY;
        }
      });
      canvas.on("mouse:move", function (opt) {
        const e = opt.e;
        const vpt = this.viewportTransform;
        // if (this.isDragging) {
        //   const backgroundImage = this.backgroundImage;
        //   if (!e.target || typeof e.target.matches !== 'function' || !e.target.matches('canvas')) {
        //     return
        //   }
        //   let deltaX, deltaY, lastX, lastY;
        //   if (e.type === "mousemove") {
        //     deltaX =  e.clientX - this.lastPosX;
        //     deltaY =  e.clientY - this.lastPosY;
        //     lastX = e.clientX;
        //     lastY = e.clientY;
        //   } else if (e.type === "touchmove" && e.touches.length) {
        //     deltaX = e.touches[0].clientX - this.lastPosX;
        //     deltaY = e.touches[0].clientY - this.lastPosY;
        //     lastX = e.touches[0].clientX;
        //     lastY = e.touches[0].clientY;
        //   }
        //   if (backgroundImage) {
        //     const { width: imgwidth, height: imgheight } = backgroundImage;
        //     const minX = imgwidth;
        //     const minY = imgheight;
        //     const signX = (vpt[4] > 0 ? 1 : -1);
        //     const signY = (vpt[5] > 0 ? 1 : -1);
        //     if ((Math.abs(vpt[4])) / vpt[0] + (imgwidth * 0.2) > minX) {
        //       deltaX = - 1 * signX;
        //     }

        //     if ((Math.abs(vpt[5])) / vpt[0] + (imgheight * 0.2) > minY) {
        //       deltaY = -1 * signY;
        //     }
        //   }
        //   vpt[4] += deltaX;
        //   vpt[5] += deltaY;
        //   this.requestRenderAll();
        //   this.lastPosX = lastX;
        //   this.lastPosY = lastY;
        // }
        if (this.isDragging) {
          if (e.type === "mousemove") {
            vpt[4] += e.clientX - this.lastPosX;
            vpt[5] += e.clientY - this.lastPosY;
            this.requestRenderAll();
            this.lastPosX = e.clientX;
            this.lastPosY = e.clientY;
          }
          if (e.type === "touchmove" && e.touches && e.touches.length) {
            vpt[4] += e.touches[0].clientX - this.lastPosX;
            vpt[5] += e.touches[0].clientY - this.lastPosY;
            this.requestRenderAll();
            this.lastPosX = e.touches[0].clientX;
            this.lastPosY = e.touches[0].clientY;
          }
        }
      });
      canvas.on("mouse:up", function (opt) {
        // on mouse up we want to recalculate new interaction
        // for all objects, so we call setViewportTransform
        this.setViewportTransform(this.viewportTransform);
        this.isDragging = false;
        this.selection = true;
      });
    }
    if (mode === "pin") {
      canvas.on("mouse:down", (options) => {
        addPin(options, canvas, worksiteTask);
        setMode("select");
      });
    }
    if (mode === "fitbg") {
      // internal method set on useCanvasInit
      canvas._methods.zoomAndPanToFitImage(canvas, canvas.backgroundImage);
      canvas.renderAll();
      setMode("move");
    }
    if (mode === "highlight") {
      canvas.on("path:created", (e) => {
        // Send the path to the back
        e.path.moveTo(0);

        // Bring the pin back to the top
        const pin = canvas
          .getObjects()
          .find((obj) => obj.get("name") === "pin");
        if (pin) {
          pin.moveTo(1000);
        }

        // Render the updated canvas
        canvas.renderAll();
      });

      const highlightColor = COLORS[color];
      setupDrawingMode(canvas, highlightColor);
    } else {
      // If not in highlight mode, disable drawing mode
      canvas.isDrawingMode = false;
    }
  }, [
    preSignedUrl,
    mode,
    canvas,
    color,
    worksiteTask,
    handleSaveButtonClick,
    setMode,
    onClickElement,
    cacheBaloonResponses,
    countryApiRequest,
  ]);

  return [
    setupDrawingMode,
    hasSelectedObject,
    countryApiRequest,
    cacheBaloonResponses,
    deleteSelectedObject,
    serializeCanvasState,
    handleSaveButtonClick,
    setMode,
  ];
};

export default useCanvasMode;
